import { Injectable, OnDestroy } from '@angular/core';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { Location } from '@angular/common';
import { filter } from 'rxjs/operators';
import { Subscription } from 'rxjs';

interface MicrofrontendNavigateEvent {
  event: 'push' | 'replace' | 'back';
  path?: string;
  search?: string;
  // SA-9315: `null` is an MFE's explicit "remove this param" signal (see salve-web-mono's
  // clinic-messaging `writeUnassignedParam`) — `queryParamsHandling: 'merge'` below only ever
  // touches keys present in this object, so an omitted key can't clear a stale param already in
  // the URL. Angular's Router already treats a `null` merged value as removal
  // (`Router.removeEmptyProps` runs after the merge), so no handling code changes, only the type.
  query?: Record<string, string | null>;
}

@Injectable({
  providedIn: 'root'
})
export class MicrofrontendNavigationService implements OnDestroy {
  private routerSubscription: Subscription | null = null;

  constructor(
    private router: Router,
    private location: Location,
    private activatedRoute: ActivatedRoute
  ) {
    this.setupEventListener();
    this.subscribeToRouteChanges();
  }

  private setupEventListener(): void {
    window.addEventListener(
      'microfrontendNavigate',
      this.handleMicrofrontendNavigate as EventListener
    );
  }

  private subscribeToRouteChanges(): void {
    this.routerSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        // Get all route params (including nested routes)
        const params = this.getAllRouteParams(this.activatedRoute);

        // Dispatch custom event to microfrontend
        const customEvent = new CustomEvent('microfrontendRouteChange', {
          detail: { params }
        });
        window.dispatchEvent(customEvent);
      });
  }

  private getAllRouteParams(route: ActivatedRoute): Record<string, string> {
    let params: Record<string, string> = {};

    // Traverse the route tree to collect all params
    let currentRoute: ActivatedRoute | null = route;
    while (currentRoute) {
      if (currentRoute.snapshot.params) {
        params = { ...params, ...currentRoute.snapshot.params };
      }
      if (currentRoute.snapshot.queryParams) {
        params = { ...params, ...currentRoute.snapshot.queryParams };
      }
      currentRoute = currentRoute.firstChild;
    }

    return params;
  }

  private handleMicrofrontendNavigate = (event: CustomEvent): void => {
    const payload = event.detail as MicrofrontendNavigateEvent;
    switch (payload.event) {
      case 'push':
        if (payload.path) {
          // Construct query params if provided
          const queryParams = payload.query || {};
          // Navigate to the specified path with query params
          this.router.navigate([payload.path], {
            queryParams,
            queryParamsHandling: 'merge'
          });
        }
        break;
      case 'replace':
        if (payload.path) {
          // Construct query params if provided
          const queryParams = payload.query || {};
          // Navigate to the specified path with query params using replaceUrl
          this.router.navigate([payload.path], {
            queryParams,
            queryParamsHandling: 'merge',
            replaceUrl: true
          });
        }
        break;
      case 'back':
        this.location.back();
        break;
    }
  };

  ngOnDestroy(): void {
    // Clean up event listener
    window.removeEventListener(
      'microfrontendNavigate',
      this.handleMicrofrontendNavigate as EventListener
    );

    // Clean up router subscription
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }
}
